import type { GridColumns } from '@mui/x-data-grid-pro'

import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { snapshot } from 'valtio'

import { ordersGridAtom } from '@/atoms'
import { selectTerritory } from '@/features/domain/territory'

import { AddressCell } from '../../components/AddressCell'
import { AssignedToCell } from '../../components/AssignedToCell'
import { NameCell } from '../../components/NameCell'
import { useTexts } from '../../useTexts'

import { createCustomFieldColumn } from './createCustomFieldColumn'
import { createExecutedCustomFieldColumn } from './createExecutedCustomFieldColumn'
import { createExecutedLoadColumn } from './createExecutedLoadColumn'
import { createGenericColumn } from './createGenericColumn'
import { createLoadColumn } from './createLoadColumn'
import { getLoadsCustomFieldsInOrders } from './getLoadsCustomFieldsInOrders'
import { getShortFieldName } from './getShortFieldName'
import { getValue } from './getValue'
import { useGetCommunicatedEtaRangeValue } from './useGetCommunicatedEtaRangeValue'
import { useGetCompanyValue } from './useGetCompanyValue'
import { useGetEligibilityValue } from './useGetEligibilityValue'
import { useGetPlannedTimeValue } from './useGetPlannedTimeValue'
import { useGetRecurrenceValue } from './useGetRecurrenceValue'
import { useGetScheduleTimeValue } from './useGetScheduleTimeValue'
import { useGetTimeWindowValue } from './useGetTimeWindowValue'

function barcodeSortComparator(v1: string, v2: string): number {
  const getSortValue = (barcodeText: string) => {
    const numbers = barcodeText.match(/\d+/g)?.map(Number) || []

    // Case: "10 (new)" - only driver barcodes, no planned barcodes
    const isOnlyNewBarcodes = barcodeText.includes('(new)') && !barcodeText.includes('/')
    if (isOnlyNewBarcodes) {
      return numbers[0] || 0 // Just return the new barcode count
    }

    // Case: "18/20 Scanned" or "18/20 Scanned + 5 (new)"
    const scannedCount = numbers[0] || 0
    const totalPlannedCount = numbers[1] || 1
    const newBarcodesCount = numbers[2] || 0

    const completionPercentage = scannedCount / totalPlannedCount

    // Scale percentage by 1000 to prioritize it over new barcode count
    return completionPercentage * 1000 + newBarcodesCount
  }

  return getSortValue(v1) - getSortValue(v2)
}

export function useGridColumns(rows: uui.domain.client.rm.FormattedData[]) {
  const getTimeWindowValue = useGetTimeWindowValue()
  const { enableDynamicLoads, enableDynamicCustomFields } = useSelector(selectTerritory)
  const getScheduleTimeValue = useGetScheduleTimeValue()
  const getCommunicatedEtaRangeValue = useGetCommunicatedEtaRangeValue()
  const getPlannedTimeValue = useGetPlannedTimeValue()
  const getCompanyValue = useGetCompanyValue()
  const getEligibilityValue = useGetEligibilityValue()
  const getRecurrenceValue = useGetRecurrenceValue()
  const texts = useTexts()

  return useMemo(() => {
    const {
      settings: { columns: atomsColumn },
    } = snapshot(ordersGridAtom)

    const {
      loadsInOrders,
      executedLoadsInOrders,
      customFieldInOrders,
      executedCustomFieldInOrders,
    } = getLoadsCustomFieldsInOrders(rows)

    const createdColumnIds = new Set<string>()

    // Fixed columns
    const columns = atomsColumn.reduce<GridColumns>((acc, colDef) => {
      createdColumnIds.add(colDef.field)

      switch (colDef.field) {
        case 'formattedData.name':
          acc.push({
            valueGetter: getValue,
            headerName: texts[getShortFieldName(colDef.field)],
            renderCell: NameCell,
            field: colDef.field,
            hide: false,
            width: colDef.width || 200,
            filterable: false,
          })
          break

        case 'formattedData.orderId':
          acc.push({
            headerName: texts[getShortFieldName(colDef.field)],
            valueGetter: getValue,
            field: colDef.field,
            hide: false,
            width: colDef.width || 200,
            filterable: false,
          })
          break

        case 'formattedData.address':
          acc.push({
            headerName: texts[getShortFieldName(colDef.field)],
            renderCell: AddressCell,
            valueGetter: getValue,
            field: colDef.field,
            hide: colDef.hide,
            width: colDef.width || 200,
            filterable: false,
          })
          break

        case 'formattedData.assignedTo':
          acc.push({
            headerName: texts[getShortFieldName(colDef.field)],
            renderCell: AssignedToCell,
            valueGetter: getValue,
            field: colDef.field,
            hide: colDef.hide,
            width: colDef.width || 200,
            filterable: false,
          })
          break

        case 'formattedData.scheduleTime':
          acc.push({
            headerName: texts[getShortFieldName(colDef.field)],
            width: colDef.width || 200,
            valueGetter: getScheduleTimeValue,
            field: colDef.field,
            hide: colDef.hide,
            filterable: false,
          })
          break

        case 'formattedData.company':
          acc.push({
            headerName: texts[getShortFieldName(colDef.field)],
            width: colDef.width || 200,
            valueGetter: getCompanyValue,
            field: colDef.field,
            hide: colDef.hide,
            filterable: false,
          })
          break

        case 'formattedData.plannedTime':
          acc.push({
            headerName: texts[getShortFieldName(colDef.field)],
            width: colDef.width || 200,
            valueGetter: getPlannedTimeValue,
            field: colDef.field,
            hide: colDef.hide,
            filterable: false,
          })
          break

        case 'formattedData.communicatedEtaRanges.twoDaysNotification':
        case 'formattedData.communicatedEtaRanges.sameDayNotification':
        case 'formattedData.communicatedEtaRanges.oneDayNotification':
          acc.push({
            headerName: texts[getShortFieldName(colDef.field)],
            width: colDef.width || 200,
            valueGetter: getCommunicatedEtaRangeValue,
            field: colDef.field,
            hide: colDef.hide,
            filterable: false,
          })
          break

        case 'formattedData.timeWindow1':
        case 'formattedData.timeWindow2':
          acc.push({
            headerName: texts[getShortFieldName(colDef.field)],
            width: colDef.width || 200,
            valueGetter: getTimeWindowValue,

            field: colDef.field,
            hide: colDef.hide,
            filterable: false,
          })
          break

        case 'formattedData.eligibility':
          acc.push({
            headerName: texts[getShortFieldName(colDef.field)],
            width: colDef.width || 200,
            valueGetter: getEligibilityValue,
            field: colDef.field,
            hide: colDef.hide,
            filterable: false,
          })
          break

        case 'formattedData.recurrence':
          acc.push({
            headerName: texts[getShortFieldName(colDef.field)],
            width: colDef.width || 200,
            valueGetter: getRecurrenceValue,
            field: colDef.field,
            hide: colDef.hide,
            filterable: false,
          })
          break

        case 'formattedData.barcodes':
          acc.push({
            headerName: texts[getShortFieldName(colDef.field)],
            width: colDef.width || 200,
            valueGetter: getValue,
            field: colDef.field,
            hide: colDef.hide,
            filterable: false,
            sortComparator: barcodeSortComparator,
          })
          break

        case 'formattedData.shopifyOrderName':
          acc.push({
            headerName: texts[getShortFieldName(colDef.field)],
            width: colDef.width || 200,
            valueGetter: getValue,
            field: colDef.field,
            hide: colDef.hide,
            filterable: false,
            sortComparator: barcodeSortComparator,
          })
          break

        case 'formattedData.requiredVehicle':
        case 'formattedData.addressCheck':
        case 'formattedData.requiredTags':
        case 'formattedData.serviceTime':
        case 'formattedData.bannedTags':
        case 'formattedData.orderType':
        case 'formattedData.idleTime':
        case 'formattedData.priority':
        case 'formattedData.pairId':
        case 'formattedData.status':
        case 'formattedData.stopNo':
        case 'formattedData.notes':
        case 'formattedData.phone':
        case 'formattedData.email':
          acc.push({
            headerName: texts[getShortFieldName(colDef.field)],
            width: colDef.width || 200,
            valueGetter: getValue,
            field: colDef.field,
            hide: colDef.hide,
            filterable: false,
          })
          break

        default:
          const genericColumn = createGenericColumn(
            colDef,
            texts,
            enableDynamicLoads,
            enableDynamicCustomFields,
            loadsInOrders,
            executedLoadsInOrders,
            customFieldInOrders,
            executedCustomFieldInOrders,
          )
          if (genericColumn) {
            acc.push(genericColumn)
          }
      }
      return acc
    }, [])

    // Dynamic columns
    for (const row of rows) {
      // ----------------------------------------------
      // Loads
      // ----------------------------------------------

      for (const loadName in row.loads) {
        const columnName = `formattedData.loads.${loadName}`
        // If the column has already been pushed continue
        if (createdColumnIds.has(columnName)) continue

        // Try to find if the column is stored in the atom
        const column = atomsColumn.find(col => col.field === loadName)

        columns.push(
          createLoadColumn(
            {
              field: column?.field ?? columnName,
              hide: column?.hide,
              width: column?.width,
            },
            texts,
            enableDynamicLoads,
          ),
        )
        createdColumnIds.add(columnName)
      }

      if (enableDynamicLoads) {
        for (const executedLoadName in row.executedLoads) {
          const columnName = `formattedData.executedLoads.${executedLoadName}`
          // If the column has already been pushed continue
          if (createdColumnIds.has(columnName)) continue

          // Try to find if the column is stored in the atom
          const column = atomsColumn.find(col => col.field === executedLoadName)

          columns.push(
            createExecutedLoadColumn(
              {
                field: column?.field ?? columnName,
                hide: column?.hide,
                width: column?.width,
              },
              texts,
            ),
          )
          createdColumnIds.add(columnName)
        }
      }

      // ----------------------------------------------
      // Custom fields
      // ----------------------------------------------
      for (const customField in row.customFields) {
        const columnName = `formattedData.customFields.${customField}`
        // If the column has already been pushed continue
        if (createdColumnIds.has(columnName)) continue

        // Try to find if the column is stored in the atom
        const column = atomsColumn.find(col => col.field === customField)

        columns.push(
          createCustomFieldColumn(
            {
              field: column?.field ?? columnName,
              hide: column?.hide,
              width: column?.width,
            },
            texts,
            enableDynamicCustomFields,
          ),
        )
        createdColumnIds.add(columnName)
      }

      if (enableDynamicCustomFields) {
        for (const customField in row.executedCustomFields) {
          const columnName = `formattedData.executedCustomFields.${customField}`
          // If the column has already been pushed continue
          if (createdColumnIds.has(columnName)) continue

          // Try to find if the column is stored in the atom
          const column = atomsColumn.find(col => col.field === customField)

          columns.push(
            createExecutedCustomFieldColumn(
              {
                field: column?.field ?? columnName,
                hide: column?.hide,
                width: column?.width,
              },
              texts,
            ),
          )
          createdColumnIds.add(columnName)
        }
      }
    }

    return columns
  }, [
    enableDynamicCustomFields,
    getScheduleTimeValue,
    getPlannedTimeValue,
    enableDynamicLoads,
    getCompanyValue,
    texts,
    rows,
    getTimeWindowValue,
    getCommunicatedEtaRangeValue,
    getEligibilityValue,
  ])
}
